import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useGummiwormContext } from "@/context/GummiwormContext";
import { TDepositHistoryStatus } from "@/context/GummiwormContext/hooks/useDepositTxs";
import { formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";

const Countdown: FC<{ target: number }> = ({ target }) => {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, target - Math.floor(Date.now() / 1000)),
  );

  useEffect(() => {
    const tick = () =>
      setRemaining(Math.max(0, target - Math.floor(Date.now() / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  if (remaining <= 0) return null;
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const text = m > 0 ? `~${m}m ${String(s).padStart(2, "0")}s` : `~${s}s`;
  return <span className="text-xs text-sr-lemon-muted tabular-nums">{text}</span>;
};

const statusConfig: Record<
  TDepositHistoryStatus,
  { label: string; color: string; spinning?: boolean }
> = {
  new: { label: "Submitting", color: "text-sr-neutral-500", spinning: true },
  pending: { label: "Waiting for approval", color: "text-sr-neutral-500", spinning: true },
  confirmed: { label: "Submitting", color: "text-sr-sky", spinning: true },
  settling: { label: "Submitting", color: "text-sr-sky", spinning: true },
  "on-chain": { label: "Waiting for settlement", color: "text-sr-sky", spinning: true },
  absorbed: { label: "Available", color: "text-sr-mint" },
  rejected: { label: "Rejected", color: "text-sr-salmon" },
  refunding: { label: "Refunding", color: "text-sr-tangerine", spinning: true },
  refunded: { label: "Refunded", color: "text-sr-tangerine" },
  failed: { label: "Failed", color: "text-sr-salmon" },
};

interface GummiwormBalanceProps {
  onViewDeposit?: (requestId: string) => void;
}

export const GummiwormBalance: FC<GummiwormBalanceProps> = ({ onViewDeposit }) => {
  const { deposits, depositTxs, clearDepositTxs, myAccountId } = useGummiwormContext();
  const [showAll, setShowAll] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

  const toggleError = useCallback((id: string) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const filteredDeposits = useMemo(
    () => showAll || myAccountId == null
      ? deposits
      : deposits.filter((d) => d.accountId === myAccountId),
    [deposits, showAll, myAccountId],
  );

  // Track all SpacetimeDB deposit IDs so the local-only loop doesn't
  // re-add deposits that exist in the DB but were filtered out.
  const allDepositIds = useMemo(
    () => new Set(deposits.map((d) => d.requestId.toString())),
    [deposits],
  );

  const allDeposits = useMemo(() => {
    const items: {
      id: string;
      amountLabel: string | null;
      status: TDepositHistoryStatus;
      hash?: string;
      errorMessage?: string;
      estimatedAbsorptionTime?: number;
    }[] = [];

    for (const dep of filteredDeposits) {
      const txData = depositTxs.get(dep.requestId.toString());
      const assets = dep.value.assets;
      let amountLabel: string | null = null;
      if (assets.length > 0) {
        const first = assets[0];
        const primary = `${formatAssetValue(first.asset, first.value)} ${formatAssetLabel(first.asset)}`;
        amountLabel = assets.length > 1
          ? `${primary} + ${assets.length - 1} other${assets.length > 2 ? "s" : ""}`
          : primary;
      }

      // Derive status from the DB row if no local tracking exists
      const status: TDepositHistoryStatus = txData?.status
        ?? (dep.absorbedInBlock ? "absorbed"
          : dep.rejectedInBlock ? "rejected"
          : dep.failureReason ? "failed"
          : dep.refundTx ? "confirmed"
          : "pending");

      items.push({
        id: dep.requestId.toString(),
        amountLabel,
        status,
        hash: txData?.hash || undefined,
        errorMessage: txData?.errorMessage ?? dep.failureReason ?? undefined,
        estimatedAbsorptionTime: txData?.estimatedAbsorptionTime,
      });
    }

    // Add local-only deposits not yet in SpacetimeDB (only when viewing own)
    if (!showAll) {
      for (const [id, tx] of depositTxs.entries()) {
        if (!allDepositIds.has(id)) {
          items.push({ id, amountLabel: null, status: tx.status });
        }
      }
    }

    return items.sort((a, b) => Number(b.id) - Number(a.id));
  }, [filteredDeposits, depositTxs, showAll, allDepositIds]);

  if (allDeposits.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* Deposit history */}
      {allDeposits.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between px-1 mb-2">
            <span className="text-xs text-sr-neutral-500 uppercase tracking-wider">
              Recent Deposits
            </span>
            {myAccountId != null && (
              <div className="flex items-center gap-1 bg-sr-surface rounded-full p-0.5">
                <button
                  onClick={() => setShowAll(false)}
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                    !showAll
                      ? "bg-sr-surface-raised text-white"
                      : "text-sr-neutral-500 hover:text-sr-neutral-400"
                  }`}
                >
                  Mine
                </button>
                <button
                  onClick={() => setShowAll(true)}
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                    showAll
                      ? "bg-sr-surface-raised text-white"
                      : "text-sr-neutral-500 hover:text-sr-neutral-400"
                  }`}
                >
                  All
                </button>
              </div>
            )}
          </div>
          {allDeposits.map((dep) => {
            const cfg = statusConfig[dep.status];
            const isError = dep.status === "rejected" || dep.status === "failed";
            const isInProgress = cfg.spinning;
            const isExpandable =
              isError || dep.status === "refunding" || dep.status === "refunded";
            const isViewable = dep.status === "absorbed";
            const isClickable = isExpandable || isInProgress || isViewable;
            const isExpanded = expandedErrors.has(dep.id);

            const handleClick = () => {
              if ((isInProgress || isViewable) && onViewDeposit) {
                onViewDeposit(dep.id);
              } else if (isExpandable) {
                toggleError(dep.id);
              }
            };

            return (
              <div key={dep.id}>
                <div
                  className={`flex items-center justify-between p-3 rounded-lg bg-sr-surface transition-colors ${
                    isClickable ? "cursor-pointer hover:bg-sr-surface-raised" : ""
                  }`}
                  onClick={isClickable ? handleClick : undefined}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {/* Status indicator */}
                    {cfg.spinning ? (
                      <div className="w-4 h-4 shrink-0 rounded-full border-2 border-current border-t-transparent animate-spin text-sr-neutral-500" />
                    ) : dep.status === "absorbed" ? (
                      <div className="w-4 h-4 shrink-0 rounded-full bg-sr-mint flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#170B10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : isError ? (
                      <div className="w-4 h-4 shrink-0 rounded-full bg-sr-salmon flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3L9 9M9 3L3 9" stroke="#170B10" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-4 h-4 shrink-0 rounded-full bg-sr-tangerine flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M6 3V6.5M6 8.5V9" stroke="#170B10" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                    )}

                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-white truncate">
                        Deposit #{dep.id}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {isInProgress && dep.estimatedAbsorptionTime && (
                          <Countdown target={dep.estimatedAbsorptionTime} />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {dep.amountLabel && (
                      <span className="text-sm tabular-nums text-white">
                        {dep.amountLabel}
                      </span>
                    )}
                    {dep.hash && (
                      <a
                        href={`https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${dep.hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-sr-neutral-500 hover:text-sr-oat-milk transition-colors"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M5.5 2.5H3.5C2.94772 2.5 2.5 2.94772 2.5 3.5V10.5C2.5 11.0523 2.94772 11.5 3.5 11.5H10.5C11.0523 11.5 11.5 11.0523 11.5 10.5V8.5M8.5 2.5H11.5V5.5M11.5 2.5L6.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>

                {/* Expandable error */}
                {isExpandable && isExpanded && dep.errorMessage && (
                  <div className="mx-3 px-3 py-2 text-xs text-sr-salmon bg-sr-salmon/10 rounded-b-lg -mt-1">
                    {dep.errorMessage}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reset local deposit state */}
      {allDeposits.length > 0 && (
        <button
          onClick={clearDepositTxs}
          className="text-[10px] text-sr-neutral-700 hover:text-sr-neutral-500 transition-colors self-end px-1"
        >
          Reset deposit history
        </button>
      )}
    </div>
  );
};
